import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { cn } from '@/lib/cn';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { type FocusSettings, type FocusState, useFocus } from './useFocus';

type SettingsPatch = Partial<FocusSettings> & { pomodoro_enabled?: boolean };

export function PomodoroSettingsPanel() {
  const focus = useFocus();
  const save = useSaveFocusSettings();

  const [workMinutes, setWorkMinutes] = useState('25');
  const [shortBreak, setShortBreak] = useState('5');
  const [longBreak, setLongBreak] = useState('15');
  const [longBreakEvery, setLongBreakEvery] = useState('4');
  const [dailyGoal, setDailyGoal] = useState('8');
  const [pomodoroEnabled, setPomodoroEnabled] = useState(true);
  const [autoStartBreaks, setAutoStartBreaks] = useState(true);
  const [autoStartWork, setAutoStartWork] = useState(false);
  const [pauseOnBreak, setPauseOnBreak] = useState(true);

  const [blockDuration, setBlockDuration] = useState('120');
  const [blockPlanning, setBlockPlanning] = useState('3');
  const [blockSprints, setBlockSprints] = useState('3');

  useEffect(() => {
    if (!focus.data) return;
    const s = focus.data.settings;
    setWorkMinutes(String(s.work_minutes));
    setShortBreak(String(s.short_break_minutes));
    setLongBreak(String(s.long_break_minutes));
    setLongBreakEvery(String(s.long_break_every));
    setDailyGoal(String(s.daily_goal_pomodoros));
    setBlockDuration(String(s.block_duration_minutes ?? 120));
    setBlockPlanning(String(s.block_planning_minutes ?? 3));
    setBlockSprints(String(s.block_sprints_per_block ?? 3));
    setPomodoroEnabled(focus.data.pomodoro_enabled);
    setAutoStartBreaks(s.auto_start_breaks);
    setAutoStartWork(s.auto_start_next_work);
    setPauseOnBreak(s.pause_task_on_break);
  }, [focus.data]);

  if (!focus.data) {
    return <div className="h-40 animate-pulse rounded-lg bg-surface-2" aria-hidden />;
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const patch: SettingsPatch = {
      work_minutes: Number(workMinutes),
      short_break_minutes: Number(shortBreak),
      long_break_minutes: Number(longBreak),
      long_break_every: Number(longBreakEvery),
      daily_goal_pomodoros: Number(dailyGoal),
      block_duration_minutes: Number(blockDuration),
      block_planning_minutes: Number(blockPlanning),
      block_sprints_per_block: Number(blockSprints),
      pomodoro_enabled: pomodoroEnabled,
      auto_start_breaks: autoStartBreaks,
      auto_start_next_work: autoStartWork,
      pause_task_on_break: pauseOnBreak,
    };

    save.mutate(patch, {
      onSuccess: () => toast.show('Focus settings saved', { tone: 'success' }),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not save'),
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-4 rounded-lg border border-border bg-surface-1 p-4"
    >
      <ToggleRow
        label="Pomodoro timer"
        description="Work/break intervals with a daily goal. When off, focus still tracks task time."
        checked={pomodoroEnabled}
        onChange={setPomodoroEnabled}
      />

      <div
        className={cn(
          'grid grid-cols-1 gap-4 sm:grid-cols-2',
          !pomodoroEnabled && 'pointer-events-none opacity-50',
        )}
      >
        <Field
          label="Work interval (minutes)"
          type="number"
          min={1}
          max={240}
          value={workMinutes}
          onChange={(e) => setWorkMinutes(e.target.value)}
          required
        />
        <Field
          label="Short break (minutes)"
          type="number"
          min={1}
          max={120}
          value={shortBreak}
          onChange={(e) => setShortBreak(e.target.value)}
          required
        />
        <Field
          label="Long break (minutes)"
          type="number"
          min={1}
          max={240}
          value={longBreak}
          onChange={(e) => setLongBreak(e.target.value)}
          required
        />
        <Field
          label="Long break every N pomodoros"
          type="number"
          min={1}
          max={12}
          value={longBreakEvery}
          onChange={(e) => setLongBreakEvery(e.target.value)}
          required
        />
        <Field
          label="Daily pomodoro goal"
          type="number"
          min={1}
          max={30}
          value={dailyGoal}
          onChange={(e) => setDailyGoal(e.target.value)}
          required
        />
      </div>

      <div className="border-t border-border pt-4">
        <h3 className="mb-3 text-body font-medium">Work blocks</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field
            label="Block duration (min)"
            type="number"
            min={30}
            max={480}
            value={blockDuration}
            onChange={(e) => setBlockDuration(e.target.value)}
          />
          <Field
            label="Planning (min)"
            type="number"
            min={1}
            max={30}
            value={blockPlanning}
            onChange={(e) => setBlockPlanning(e.target.value)}
          />
          <Field
            label="Sprints per block"
            type="number"
            min={1}
            max={8}
            value={blockSprints}
            onChange={(e) => setBlockSprints(e.target.value)}
          />
        </div>
        <p className="mt-2 text-small text-text-tertiary">
          Default sprint rhythm: {workMinutes}m work / {shortBreak}m break (×{blockSprints})
        </p>
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <ToggleRow
          label="Auto-start breaks"
          description="Begin the break interval when work ends."
          checked={autoStartBreaks}
          onChange={setAutoStartBreaks}
          disabled={!pomodoroEnabled}
        />
        <ToggleRow
          label="Auto-start next work interval"
          description="Resume work after a break without an extra tap."
          checked={autoStartWork}
          onChange={setAutoStartWork}
          disabled={!pomodoroEnabled}
        />
        <ToggleRow
          label="Pause task on break"
          description="Stop the task clock during breaks so time stays honest."
          checked={pauseOnBreak}
          onChange={setPauseOnBreak}
          disabled={!pomodoroEnabled}
        />
      </div>

      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Save focus settings
        </Button>
      </div>
    </form>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start justify-between gap-4',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span className="min-w-0">
        <span className="block text-body font-medium text-text">{label}</span>
        <span className="mt-0.5 block text-small text-text-secondary">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 size-5 shrink-0 accent-accent"
      />
    </label>
  );
}

function useSaveFocusSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) =>
      api<FocusSettings & { pomodoro_enabled?: boolean }>('/api/focus/settings', {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: (_data, patch) => {
      qc.setQueryData<FocusState>(['focus'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pomodoro_enabled: patch.pomodoro_enabled ?? prev.pomodoro_enabled,
          daily_goal_pomodoros: patch.daily_goal_pomodoros ?? prev.daily_goal_pomodoros,
          settings: { ...prev.settings, ...patch },
        };
      });
      void qc.invalidateQueries({ queryKey: ['focus'] });
    },
  });
}
